import React, { useCallback, useId } from "react";
import { Entity } from "@common/types/entity";
import {
    DEFAULT_INVOICE_FONT_ID,
    INVOICE_BACKGROUND_PRESETS,
    INVOICE_DEFAULT_TEXT_COLOR,
    INVOICE_FONTS,
    INVOICE_TEXT_COLOR_PRESETS,
    normalizeHexColor,
} from "@common/constants/invoiceDesign";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Switch } from "@design-system/Switch/Switch";
import { Field } from "@design-system/Field/Field";
import { useNativeInvoiceFormContext } from "./NativeInvoiceFormContext";
import { CustomColorTile, SwatchTile } from "./DesignSectionSwatches";
import { LogoUploadRow } from "./LogoUploadRow";
import { NativeInvoiceFooterField } from "./NativeInvoiceFooterField";
import { NativeInvoicePageSizeField } from "./NativeInvoicePageSizeField";
import {
    DEFAULT_CUSTOM_BACKGROUND,
    isCustomBackgroundColor,
    isCustomTextColor,
    isPresetSelected,
} from "./designSectionHelpers";
import styles from "./DesignSection.module.scss";
import panelStyles from "./SidePanel.module.scss";
import { HStack } from "designSystem/HStack/HStack";
import { VStack } from "designSystem/VStack/VStack";

interface Props {
    selectedEntity?: Entity;
}

export const DesignSection: React.FC<Props> = ({ selectedEntity }) => {
    const { state, setState } = useNativeInvoiceFormContext();
    const paginationLabelId = useId();

    const setBackgroundColor = useCallback(
        (backgroundColor: string | null) => {
            setState((prev) => ({
                ...prev,
                backgroundColor,
                accentColor: null,
            }));
        },
        [setState],
    );

    const setTextColor = useCallback(
        (textColor: string | null) => {
            setState((prev) => ({
                ...prev,
                textColor,
            }));
        },
        [setState],
    );

    const customBackgroundValue = state.backgroundColor
        ? normalizeHexColor(state.backgroundColor)
        : DEFAULT_CUSTOM_BACKGROUND;

    // Any background that isn't white or one of the presets belongs to the
    // custom tile, so the selection needs no state of its own.
    const isCustomBackground = isCustomBackgroundColor(state.backgroundColor);

    const customTextColorValue = state.textColor
        ? normalizeHexColor(state.textColor)
        : INVOICE_DEFAULT_TEXT_COLOR;

    // Same custom-tile rule as the background swatches: anything that isn't
    // the default or one of the presets belongs to the color-wheel tile.
    const isCustomTextColorValue = isCustomTextColor(state.textColor);

    return (
        <div className={styles.designSection}>
            <div className={panelStyles.fieldGrid}>
                <LogoUploadRow selectedEntity={selectedEntity} />

                <div className={styles.section}>
                    <span className={styles.sectionLabel}>Background</span>
                    <div className={styles.swatchGrid}>
                        <SwatchTile
                            label="Printer friendly"
                            selected={state.backgroundColor == null}
                            onClick={() => setBackgroundColor(null)}
                        />
                        {INVOICE_BACKGROUND_PRESETS.map((preset) => (
                            <SwatchTile
                                key={preset.id}
                                label={preset.label}
                                color={preset.background}
                                selected={isPresetSelected(
                                    state.backgroundColor,
                                    preset.background,
                                )}
                                onClick={() =>
                                    setBackgroundColor(preset.background)
                                }
                            />
                        ))}
                        <CustomColorTile
                            label="Custom background color"
                            value={customBackgroundValue}
                            onChange={setBackgroundColor}
                            selected={isCustomBackground}
                        />
                    </div>
                </div>
            </div>

            <div className={panelStyles.fieldGrid}>
                <Field label="Font" control="button">
                    <Picker.Select
                        value={state.fontFamily ?? DEFAULT_INVOICE_FONT_ID}
                        onValueChange={(value) => {
                            if (value === null) {
                                return;
                            }
                            setState((prev) => ({
                                ...prev,
                                fontFamily: value,
                            }));
                        }}
                    >
                        <Picker.ButtonTrigger
                            render={
                                <Picker.Button variant="primary" size="xl" />
                            }
                            value={
                                INVOICE_FONTS.find(
                                    (font) => font.id === state.fontFamily,
                                )?.label ??
                                INVOICE_FONTS.find(
                                    (font) =>
                                        font.id === DEFAULT_INVOICE_FONT_ID,
                                )?.label ??
                                ""
                            }
                        />
                        <Picker.Popup>
                            <Picker.List>
                                {INVOICE_FONTS.map((font) => (
                                    <Picker.Item key={font.id} value={font.id}>
                                        <span
                                            style={{
                                                fontFamily: font.stack,
                                            }}
                                        >
                                            {font.label}
                                        </span>
                                    </Picker.Item>
                                ))}
                            </Picker.List>
                        </Picker.Popup>
                    </Picker.Select>
                </Field>
                <VStack gap={4}>
                    <span className={styles.sectionLabel}>Font color</span>
                    <div className={styles.swatchGrid}>
                        {INVOICE_TEXT_COLOR_PRESETS.map((preset) => (
                            <SwatchTile
                                key={preset.id}
                                label={preset.label}
                                color={preset.color}
                                // A fresh invoice has no override (textColor
                                // null); it renders in the default color, so the
                                // Default swatch must read as selected.
                                selected={isPresetSelected(
                                    state.textColor ??
                                        INVOICE_DEFAULT_TEXT_COLOR,
                                    preset.color,
                                )}
                                isCheckOnDark
                                onClick={() =>
                                    setTextColor(
                                        preset.color ===
                                            INVOICE_DEFAULT_TEXT_COLOR
                                            ? null
                                            : preset.color,
                                    )
                                }
                            />
                        ))}
                        <CustomColorTile
                            label="Custom font color"
                            value={customTextColorValue}
                            onChange={setTextColor}
                            selected={isCustomTextColorValue}
                            isCheckOnDark
                        />
                    </div>
                </VStack>
            </div>

            <div className={panelStyles.fieldGrid}>
                <NativeInvoicePageSizeField />
                <NativeInvoiceFooterField />
            </div>
            <HStack justify="between">
                <span id={paginationLabelId} className={styles.sectionLabel}>
                    Pagination
                </span>
                <Switch
                    checked={state.showPagination}
                    onCheckedChange={(checked) =>
                        setState((prev) => ({
                            ...prev,
                            showPagination: checked,
                        }))
                    }
                    aria-labelledby={paginationLabelId}
                />
            </HStack>
        </div>
    );
};
